import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Input,
  Select,
  Button,
  Tag,
  Modal,
  Form,
  Tooltip,
  Empty,
  Spin,
  Switch,
  Typography,
  message,
} from "antd";
import {
  Lock,
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  Search as SearchIcon,
  Unlock,
  Info,
  XCircle,
  CheckCircle,
  Clock,
  Timer,
  Bell,
  Ban,
  UserX,
} from "lucide-react";
import moment from "moment";

import {
  fetchLockoutEvents,
  fetchLockoutBadSources,
  fetchDiagnosticsStatus,
  unlockAccountViaGateway,
} from "../../service/lockoutService";

import { anAlpha } from "../../design-system/tokens";
const { Title, Text } = Typography;
const { TextArea } = Input;

const REFRESH_OPTIONS = [
  { value: 2, label: "2s" },
  { value: 5, label: "5s" },
  { value: 15, label: "15s" },
  { value: 0, label: "Off" },
];

const SHOW_ALL_KEY = "lockoutShowAllEvents";
const RECENT_KEY = "lockoutRecentSearches";
const RECENT_CAP = 8;

const readRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
};

const writeRecent = (list) => {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* localStorage unavailable — silently degrade */
  }
};

const VERDICT_COLOR = {
  deny: { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca", label: "DENY" },
  allow: { bg: "#ecfdf5", fg: "#15803d", border: "#bbf7d0", label: "ALLOW" },
  mfa_required: {
    bg: "#fff7ed",
    fg: "#c2410c",
    border: "#fed7aa",
    label: "MFA_REQUIRED",
  },
};

const STATUS_THEME = {
  locked: {
    label: "LOCKED",
    border: "#dc2626",
    bg: "#fef2f2",
    fg: "#991b1b",
    icon: <Lock size={20} />,
  },
  at_risk: {
    label: "AT RISK",
    border: "#f59e0b",
    bg: "#fffbeb",
    fg: "#b45309",
    icon: <ShieldAlert size={20} />,
  },
  unlocked: {
    label: "unlocked",
    border: "#10b981",
    bg: "#ecfdf5",
    fg: "#065f46",
    icon: <ShieldCheck size={20} />,
  },
};

const fmtTime = (val) => {
  if (!val) return "—";
  const m = typeof val === "number" ? moment(val) : moment(val);
  return m.isValid() ? m.format("DD-MM-YYYY HH:mm:ss") : "—";
};

// Icon palette for translated rows. Sized 14 so it sits inline with row text.
const ICON_DENY = <XCircle size={14} color="#dc2626" />;
const ICON_LOCK = <Lock size={14} color="#dc2626" />;
const ICON_USER_X = <UserX size={14} color="#dc2626" />;
const ICON_CLOCK = <Clock size={14} color="#f59e0b" />;
const ICON_TIMER = <Timer size={14} color="#f59e0b" />;
const ICON_BAN = <Ban size={14} color="#dc2626" />;
const ICON_OK = <CheckCircle size={14} color="#16a34a" />;
const ICON_BELL = <Bell size={14} color="#f59e0b" />;

// §A — Translation table. Keyed by (decision, reason regex, upstreamErrorCode).
// Order matters: more specific entries first, generic catch-alls last.
// Anything that doesn't match falls back to the raw reason string.
const TRANSLATION = [
  ["deny", /PREAUTH_FAILED/, 24, "Wrong password", ICON_DENY],
  ["deny", /CLIENT_REVOKED/, 18, "Account locked or disabled", ICON_LOCK],
  ["deny", /C_PRINCIPAL_UNKNOWN/, 6, "Unknown username", ICON_USER_X],
  ["deny", /CLIENT_EXPIRED/, 22, "Password expired", ICON_CLOCK],
  ["deny", /^mfa denied$/i, null, "User denied MFA on phone", ICON_DENY],
  ["deny", /^mfa timeout$/i, null, "MFA push timed out (no response)", ICON_TIMER],
  ["deny", /tgs without recent mfa/i, null, "Cached ticket reuse blocked", ICON_BAN],
  ["deny", /session_setup denied by policy/i, null, "File-share access denied by policy", ICON_BAN],
  ["allow", /^mfa approved$/i, null, "Logged in (MFA approved)", ICON_OK],
  ["allow", /^mfa session active$/i, null, "Logged in (silent — within MFA window)", ICON_OK],
  ["allow", null, null, "Logged in", ICON_OK],
];

// Translate a raw audit event into operator-facing label + icon.
// Returns { label, icon, raw } where raw=true means we fell through.
const translateEvent = (ev) => {
  if (!ev) return { label: "—", icon: null, raw: true };
  const decision = (ev.decision || "").toLowerCase();
  const reason = ev.reason || "";
  const code = typeof ev.upstreamErrorCode === "number" ? ev.upstreamErrorCode : null;
  for (const [d, rx, c, label, icon] of TRANSLATION) {
    if (d !== decision) continue;
    if (rx !== null && !rx.test(reason)) continue;
    if (c !== null && c !== code) continue;
    return { label, icon, raw: false };
  }
  if (decision === "mfa_required")
    return { label: "MFA challenge sent", icon: ICON_BELL, raw: false };
  return { label: reason || "—", icon: null, raw: true };
};

// "the last 10 minutes" / "the last hour" / "the last 3 hours" / "the last 2 days"
const humanWindow = (events) => {
  if (!events || events.length === 0) return "the recent window";
  const ts = events.map((e) => e.tsUnixMs).filter((v) => typeof v === "number");
  if (ts.length === 0) return "the recent window";
  const oldestMs = Math.min(...ts);
  const ageMs = Math.max(0, Date.now() - oldestMs);
  const minutes = ageMs / 60000;
  if (minutes < 2) return "the last minute";
  if (minutes < 60) {
    const rounded = Math.max(5, Math.ceil(minutes / 5) * 5);
    return `the last ${rounded} minutes`;
  }
  const hours = ageMs / 3600000;
  if (hours < 24) {
    const h = Math.max(1, Math.round(hours));
    return h === 1 ? "the last hour" : `the last ${h} hours`;
  }
  const days = ageMs / 86400000;
  const d = Math.max(1, Math.round(days));
  return d === 1 ? "the last day" : `the last ${d} days`;
};

// "a", "a and b", "a, b and c"
const formatList = (items) => {
  const arr = items.filter(Boolean);
  if (arr.length === 0) return "—";
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(", ")} and ${arr[arr.length - 1]}`;
};

// §B — operator-readable summary line.
const computeSummary = (events, status, principal) => {
  if (!principal) return "";
  const list = Array.isArray(events) ? events : [];
  const failures = list.filter((e) => (e.decision || "").toLowerCase() === "deny");
  if (failures.length === 0) {
    return `${principal} has no recent failures. Status: ${
      status?.locked ? "locked" : "OK"
    }.`;
  }

  const reasonCounts = new Map();
  failures.forEach((e) => {
    const t = translateEvent(e).label;
    reasonCounts.set(t, (reasonCounts.get(t) || 0) + 1);
  });
  const top = [...reasonCounts.entries()].sort((a, b) => b[1] - a[1])[0];
  const ips = [...new Set(failures.map((e) => e.clientIp).filter(Boolean))];
  const win = humanWindow(failures);

  let s = `${principal} has had ${failures.length} failed attempt${
    failures.length === 1 ? "" : "s"
  } in ${win}, from ${formatList(ips)}. `;
  s += `Most failures (${top[1]}) are ${top[0]}`;
  const otherCount = failures.length - top[1];
  s += otherCount > 0 ? `; ${otherCount} other.` : ".";

  const visibleStatus = deriveStatus(status);
  const threshold = Number(status?.lockoutThreshold) || 0;
  if (visibleStatus === "at_risk" && threshold > 0) {
    s += ` AT RISK of being locked at the ${threshold}th attempt.`;
  } else if (visibleStatus === "locked" && status?.lockoutTime) {
    s += ` Currently LOCKED since ${moment(status.lockoutTime).format(
      "HH:mm [today]"
    )}.`;
  }
  return s;
};

// Derive the visible status from diagnostics. "AT RISK" = within 1 attempt of
// lockout (e.g. bad=4 with threshold=5 ⇒ next attempt locks). Requires a
// domain policy (threshold > 0) to apply.
const deriveStatus = (status) => {
  if (!status) return null;
  if (status.locked) return "locked";
  const threshold = Number(status.lockoutThreshold) || 0;
  const bad = Number(status.badPwdCount) || 0;
  if (threshold > 0 && bad >= threshold - 1) return "at_risk";
  return "unlocked";
};

const SourceCountBadge = ({ count }) => {
  const color =
    count >= 5 ? "#dc2626" : count >= 2 ? "#f59e0b" : "#475569";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 32,
        height: 24,
        padding: "0 8px",
        borderRadius: 12,
        background: `${anAlpha(color, 10)}`,
        color,
        fontWeight: 600,
        fontSize: 12,
      }}
    >
      {count}
    </span>
  );
};

const VerdictTag = ({ decision }) => {
  const key = (decision || "").toLowerCase();
  const theme = VERDICT_COLOR[key] || {
    bg: "var(--an-line2)",
    fg: "#475569",
    border: "var(--an-line)",
    label: (decision || "—").toUpperCase(),
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        background: theme.bg,
        color: theme.fg,
        border: `1px solid ${theme.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {theme.label}
    </span>
  );
};

const LockoutDiagnostics = () => {
  const [messageApi, messageContext] = message.useMessage();
  const [principalInput, setPrincipalInput] = useState("");
  const [activePrincipal, setActivePrincipal] = useState("");
  const [refreshSec, setRefreshSec] = useState(2);

  const [events, setEvents] = useState([]);
  const [sources, setSources] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [unlockSubmitting, setUnlockSubmitting] = useState(false);
  const [unlockForm] = Form.useForm();
  // §C — toggle persists across visits in localStorage. When on, the API call
  // sends excludeIntermediate: false, excludeBenignNoise: false (raw firehose).
  const [showAllEvents, setShowAllEvents] = useState(() => {
    try {
      return localStorage.getItem(SHOW_ALL_KEY) === "true";
    } catch {
      return false;
    }
  });
  // §D — clicking a source row filters the timeline to that IP only.
  const [selectedIp, setSelectedIp] = useState(null);
  // Recent searches (operator's own history, persisted in localStorage).
  const [recentSearches, setRecentSearches] = useState(() => readRecent());

  const recordRecent = useCallback((principal) => {
    if (!principal) return;
    const lc = principal.toLowerCase();
    setRecentSearches((prev) => {
      const filtered = prev.filter((p) => p.toLowerCase() !== lc);
      const next = [principal, ...filtered].slice(0, RECENT_CAP);
      writeRecent(next);
      return next;
    });
  }, []);

  const clearRecent = useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const intervalRef = useRef(null);
  const lastPrincipalRef = useRef("");
  const showAllRef = useRef(showAllEvents);
  showAllRef.current = showAllEvents;

  const loadAll = useCallback(
    async (principal, opts = {}) => {
      if (!principal) return;
      const showSpinner = opts.showSpinner !== false;
      if (showSpinner) setLoading(true);

      const showAll = showAllRef.current;
      const eventsP = new Promise((resolve) => {
        fetchLockoutEvents({
          payload: {
            principal,
            excludeIntermediate: !showAll,
            excludeBenignNoise: !showAll,
          },
          onSuccess: (data) => resolve(data),
          onFailure: () => resolve([]),
        });
      });
      const sourcesP = new Promise((resolve) => {
        fetchLockoutBadSources({
          payload: { principal },
          onSuccess: (data) => resolve(data),
          onFailure: () => resolve([]),
        });
      });
      const statusP = new Promise((resolve) => {
        fetchDiagnosticsStatus({
          payload: { user: principal },
          onSuccess: (data) => resolve(data),
          onFailure: () => resolve({ available: false }),
        });
      });

      const [evs, srcs, st] = await Promise.all([eventsP, sourcesP, statusP]);
      setEvents(Array.isArray(evs) ? evs : []);
      setSources(Array.isArray(srcs) ? srcs : []);
      setStatus(st || { available: false });
      if (showSpinner) setLoading(false);
    },
    []
  );

  // Toggle handler: persist + refetch with the new flags.
  const handleShowAllChange = useCallback(
    (next) => {
      setShowAllEvents(next);
      try {
        localStorage.setItem(SHOW_ALL_KEY, String(next));
      } catch {
        /* localStorage unavailable — silently degrade */
      }
      if (lastPrincipalRef.current) {
        loadAll(lastPrincipalRef.current, { showSpinner: false });
      }
    },
    [loadAll]
  );

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!activePrincipal || refreshSec === 0) return undefined;
    intervalRef.current = setInterval(() => {
      loadAll(lastPrincipalRef.current, { showSpinner: false });
    }, refreshSec * 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [activePrincipal, refreshSec, loadAll]);

  const handleSearch = (raw) => {
    const principal = (raw || principalInput).trim();
    if (!principal) {
      messageApi.warning("Enter a username to search.");
      return;
    }
    lastPrincipalRef.current = principal;
    setActivePrincipal(principal);
    setPrincipalInput(principal);
    recordRecent(principal);
    loadAll(principal);
  };

  // Clearing the input drops the user back to the empty state (recent
  // searches + explainer). Stops auto-refresh by clearing activePrincipal.
  const resetSearch = useCallback(() => {
    lastPrincipalRef.current = "";
    setActivePrincipal("");
    setPrincipalInput("");
    setEvents([]);
    setSources([]);
    setStatus(null);
    setSelectedIp(null);
  }, []);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setPrincipalInput(v);
    if (v === "" && activePrincipal) resetSearch();
  };

  const handleUnlockSubmit = () => {
    unlockForm
      .validateFields()
      .then(({ reason }) => {
        setUnlockSubmitting(true);
        unlockAccountViaGateway({
          payload: { user: activePrincipal, reason },
          onSuccess: () => {
            setUnlockSubmitting(false);
            setUnlockModalOpen(false);
            unlockForm.resetFields();
            messageApi.success(`Unlock requested for ${activePrincipal}.`);
            loadAll(activePrincipal, { showSpinner: false });
          },
          onFailure: (err) => {
            setUnlockSubmitting(false);
            messageApi.error(err?.message || "Unlock failed.");
          },
        });
      })
      .catch(() => {});
  };

  const visibleStatus = useMemo(() => deriveStatus(status), [status]);
  const statusTheme = visibleStatus ? STATUS_THEME[visibleStatus] : null;

  // Backend already filters via excludeIntermediate / excludeBenignNoise. The
  // client only applies the optional source-IP filter and time-desc sort.
  const displayEvents = useMemo(() => {
    const sorted = [...events].sort(
      (a, b) => (b.tsUnixMs || 0) - (a.tsUnixMs || 0)
    );
    return selectedIp
      ? sorted.filter((e) => e.clientIp === selectedIp)
      : sorted;
  }, [events, selectedIp]);

  const summary = useMemo(
    () => computeSummary(events, status, activePrincipal),
    [events, status, activePrincipal]
  );

  const threshold = Number(status?.lockoutThreshold) || 0;
  const badCount = Number(status?.badPwdCount) || 0;
  const gatewayCount = Number(status?.gatewayFailureCount) || 0;
  const discrepancy = badCount - gatewayCount;

  const showUnlock =
    status?.available && (visibleStatus === "locked" || visibleStatus === "at_risk");

  return (
    <div style={{ padding: 24, background: "var(--an-bg)", minHeight: "100vh" }}>
      {messageContext}

      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Account Lockout Events
        </Title>
        <Text type="secondary">
          Helpdesk view — search a username to see live AD lock state, source-IP
          failures, and the recent attempt timeline.
        </Text>
      </div>

      <Card
        bodyStyle={{ padding: 16 }}
        style={{ marginBottom: 16, borderRadius: 10 }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "center",
          }}
        >
          <Input
            value={principalInput}
            onChange={handleInputChange}
            onPressEnter={() => handleSearch()}
            placeholder="Username (e.g. sathyam)"
            prefix={<SearchIcon size={14} />}
            allowClear
            style={{ maxWidth: 360 }}
          />
          <Button type="primary" onClick={() => handleSearch()}>
            Search
          </Button>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Auto-refresh
            </Text>
            <Select
              value={refreshSec}
              onChange={setRefreshSec}
              options={REFRESH_OPTIONS}
              style={{ width: 90 }}
              disabled={!activePrincipal}
            />
            <Tooltip title="Refresh now">
              <Button
                icon={<RefreshCw size={14} />}
                onClick={() =>
                  activePrincipal && loadAll(activePrincipal, { showSpinner: false })
                }
                disabled={!activePrincipal}
              />
            </Tooltip>
          </div>
        </div>
      </Card>

      {!activePrincipal ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {recentSearches.length > 0 && (
            <Card
              size="small"
              style={{ borderRadius: 10 }}
              bodyStyle={{ padding: 16 }}
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>Recent searches</span>
                  <Button
                    size="small"
                    type="link"
                    onClick={clearRecent}
                    style={{ padding: 0, fontSize: 12 }}
                  >
                    Clear history
                  </Button>
                </div>
              }
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {recentSearches.map((p) => (
                  <Tag
                    key={p}
                    color="blue"
                    style={{ cursor: "pointer", padding: "4px 10px", fontSize: 13 }}
                    onClick={() => handleSearch(p)}
                  >
                    {p}
                  </Tag>
                ))}
              </div>
            </Card>
          )}

          <Card
            style={{ borderRadius: 10 }}
            bodyStyle={{ padding: 24 }}
          >
            {recentSearches.length === 0 && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Text type="secondary">Search a username to begin.</Text>
                  }
                />
              </div>
            )}
            <Text strong style={{ display: "block", marginBottom: 12 }}>
              What you'll see here
            </Text>
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <li style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <ShieldCheck size={16} color="#10b981" style={{ marginTop: 2, flexShrink: 0 }} />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  <Text strong>Status</Text> — locked / at risk / unlocked, with the bad-attempts ratio against the domain lockout policy.
                </Text>
              </li>
              <li style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <AlertTriangle size={16} color="#f59e0b" style={{ marginTop: 2, flexShrink: 0 }} />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  <Text strong>Bad sources</Text> — failure counts grouped by client IP. Click an IP to filter the timeline to that source.
                </Text>
              </li>
              <li style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <Clock size={16} color="#475569" style={{ marginTop: 2, flexShrink: 0 }} />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  <Text strong>Recent attempts</Text> — translated, color-coded timeline. Toggle <Text code>Show all events incl. technical</Text> for the raw firehose.
                </Text>
              </li>
            </ul>
          </Card>
        </div>
      ) : (
        <Spin spinning={loading}>
          {/* Status card */}
          {statusTheme && (
            <Card
              style={{
                marginBottom: 16,
                borderRadius: 10,
                borderLeft: `4px solid ${statusTheme.border}`,
              }}
              bodyStyle={{ padding: 20 }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: statusTheme.bg,
                    color: statusTheme.fg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {statusTheme.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: statusTheme.fg,
                      letterSpacing: 0.5,
                    }}
                  >
                    {statusTheme.label}
                  </div>
                  <div style={{ color: "#475569", fontSize: 13 }}>
                    {activePrincipal}
                    {threshold > 0 ? (
                      <>
                        {"  ·  "}
                        <Text strong>
                          bad attempts: {badCount} of {threshold}
                        </Text>
                      </>
                    ) : (
                      <>
                        {"  ·  "}
                        <Text strong>bad attempts: {badCount}</Text>{" "}
                        <Text type="secondary">(no domain lockout policy)</Text>
                      </>
                    )}
                    {status?.lockoutTime ? (
                      <>
                        {"  ·  "}locked at {fmtTime(status.lockoutTime)}
                      </>
                    ) : null}
                  </div>
                </div>
                {showUnlock && (
                  <Button
                    type="primary"
                    danger
                    icon={<Unlock size={14} />}
                    onClick={() => setUnlockModalOpen(true)}
                  >
                    Unlock account
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* Summary line */}
          {summary && (
            <div
              style={{
                marginBottom: 16,
                color: "#334155",
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {summary}
            </div>
          )}

          {/* Discrepancy banner */}
          {discrepancy >= 2 && (
            <Card
              style={{
                marginBottom: 16,
                borderRadius: 10,
                background: "#fffbeb",
                border: "1px solid #fde68a",
              }}
              bodyStyle={{ padding: 12 }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <AlertTriangle size={18} color="#b45309" />
                <Text strong style={{ color: "#92400e" }}>
                  ⚠ {discrepancy} bad attempts not visible to gateway
                </Text>
                <Tooltip
                  title="AD's bad_pwd_count is higher than the gateway audit failure count. Likely causes: NTLM lateral, alternate DC handling the auth, or local SAM authentication."
                >
                  <Info size={14} color="#b45309" style={{ cursor: "help" }} />
                </Tooltip>
              </div>
            </Card>
          )}

          {/* Sources + Timeline */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(280px, 1fr) 2fr",
              gap: 16,
            }}
          >
            <Card
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>Bad sources</span>
                  {selectedIp && (
                    <Button
                      size="small"
                      type="link"
                      onClick={() => setSelectedIp(null)}
                      style={{ padding: 0, fontSize: 12 }}
                    >
                      Clear filter
                    </Button>
                  )}
                </div>
              }
              size="small"
              style={{ borderRadius: 10 }}
              bodyStyle={{ padding: 0 }}
            >
              {sources.length === 0 ? (
                <div style={{ padding: 16 }}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No bad sources."
                  />
                </div>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {sources.map((src, idx) => {
                    const ip = src.clientIp || src.sourceIp || src.ip || "—";
                    const count = Number(src.count) || 0;
                    const isSelected = selectedIp === ip;
                    // Find the most recent event from this IP and translate it.
                    const lastEvent = events
                      .filter((e) => e.clientIp === ip)
                      .sort(
                        (a, b) => (b.tsUnixMs || 0) - (a.tsUnixMs || 0)
                      )[0];
                    const lastReason = lastEvent
                      ? translateEvent(lastEvent).label
                      : null;
                    const lastTimeMs =
                      lastEvent?.tsUnixMs ||
                      src.lastSeenUnixMs ||
                      src.lastSeenAt;
                    return (
                      <li
                        key={`${ip}-${idx}`}
                        onClick={() =>
                          setSelectedIp(isSelected ? null : ip)
                        }
                        style={{
                          padding: "12px 14px",
                          borderTop:
                            idx === 0 ? "none" : "1px solid var(--an-line2)",
                          background: isSelected ? "var(--an-infoBg)" : "transparent",
                          cursor: "pointer",
                          transition: "background 120ms ease",
                        }}
                      >
                        <div style={{ fontWeight: 600, color: "#0f172a" }}>
                          {ip}
                        </div>
                        {src.hostname && (
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            {src.hostname}
                          </div>
                        )}
                        <div
                          style={{
                            height: 1,
                            background: "var(--an-line2)",
                            margin: "8px 0",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 4,
                          }}
                        >
                          <SourceCountBadge count={count} />
                          <span style={{ color: "#0f172a", fontSize: 13 }}>
                            {count === 1 ? "failure" : "failures"}
                          </span>
                        </div>
                        {lastReason && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#475569",
                              marginBottom: 2,
                            }}
                          >
                            Last: {lastReason}
                          </div>
                        )}
                        {lastTimeMs && (
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>
                            {moment(lastTimeMs).fromNow()}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>

            <Card
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span>
                    Recent attempts
                    {selectedIp && (
                      <Text
                        type="secondary"
                        style={{ fontSize: 12, marginLeft: 8, fontWeight: 400 }}
                      >
                        from {selectedIp}
                      </Text>
                    )}
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontWeight: 400,
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Show all events incl. technical
                    </Text>
                    <Switch
                      size="small"
                      checked={showAllEvents}
                      onChange={handleShowAllChange}
                    />
                  </span>
                </div>
              }
              size="small"
              style={{ borderRadius: 10 }}
              bodyStyle={{ padding: 0 }}
            >
              {displayEvents.length === 0 ? (
                <div style={{ padding: 16 }}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No events in window."
                  />
                </div>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {displayEvents.map((ev, idx) => {
                    const t = translateEvent(ev);
                    const tooltipBody = (
                      <div
                        style={{
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: 11,
                          lineHeight: 1.5,
                        }}
                      >
                        {ev.correlationId && (
                          <div>correlationId: {ev.correlationId}</div>
                        )}
                        {ev.protocol && <div>protocol: {ev.protocol}</div>}
                        {typeof ev.upstreamErrorCode === "number" && (
                          <div>upstreamErrorCode: {ev.upstreamErrorCode}</div>
                        )}
                        {ev.reason && <div>reason: {ev.reason}</div>}
                        {ev.realm && <div>realm: {ev.realm}</div>}
                      </div>
                    );
                    return (
                      <li
                        key={`${ev.correlationId || "ev"}-${ev.tsUnixMs || idx}`}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          padding: "10px 14px",
                          borderTop:
                            idx === 0 ? "none" : "1px solid var(--an-line2)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              minWidth: 150,
                              color: "#475569",
                              fontSize: 12,
                            }}
                          >
                            {fmtTime(ev.tsUnixMs)}
                          </div>
                          <VerdictTag decision={ev.decision} />
                          <Tooltip
                            title={tooltipBody}
                            placement="topLeft"
                            mouseEnterDelay={0.4}
                          >
                            <div
                              style={{
                                flex: 1,
                                color: "#0f172a",
                                fontSize: 13,
                                cursor: "help",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              {t.icon}
                              <span>{t.label}</span>
                            </div>
                          </Tooltip>
                          <Tag style={{ marginRight: 0 }}>
                            {ev.clientIp || "—"}
                          </Tag>
                          {showAllEvents && ev.protocol && (
                            <Tag color="blue" style={{ marginRight: 0 }}>
                              {ev.protocol}
                            </Tag>
                          )}
                      </div>
                      {showAllEvents && (
                        <div
                          style={{
                            color: "#94a3b8",
                            fontSize: 11,
                            paddingLeft: 162,
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, monospace",
                          }}
                        >
                          {ev.correlationId
                            ? `corr ${String(ev.correlationId).slice(0, 12)}`
                            : ""}
                          {typeof ev.upstreamErrorCode === "number"
                            ? `  ·  code ${ev.upstreamErrorCode}`
                            : ""}
                          {ev.reason ? `  ·  ${ev.reason}` : ""}
                        </div>
                      )}
                    </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </div>
        </Spin>
      )}

      <Modal
        title={`Unlock ${activePrincipal}`}
        open={unlockModalOpen}
        confirmLoading={unlockSubmitting}
        onCancel={() => {
          setUnlockModalOpen(false);
          unlockForm.resetFields();
        }}
        onOk={handleUnlockSubmit}
        okText="Unlock"
        okButtonProps={{ danger: true }}
      >
        <Form form={unlockForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason / ticket reference"
            rules={[
              {
                required: true,
                message: "A reason is required for the audit log.",
              },
              { min: 4, message: "Provide a few words of context." },
            ]}
          >
            <TextArea
              rows={3}
              placeholder="e.g. Verified via call-back, INC-12345"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LockoutDiagnostics;
